// frontend/src/pages/AboutPage.js
import React, { useState, useEffect } from 'react';
import { FaCar, FaShoppingCart, FaTools, FaBrain, FaMapMarkerAlt, FaBullseye, FaUsers, FaLightbulb, FaSpinner } from 'react-icons/fa';
import { settingsApi } from '../api/settings';
import ReactMarkdown from 'react-markdown';

function AboutPage() {
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const data = await settingsApi.getAllSettings();
        if (data && data.pages_content && data.pages_content.about_us) {
          setContent(data.pages_content.about_us);
        }
      } catch (err) {
        console.error("Failed to load about content", err);
        setError("Unable to load content.");
      } finally {
        setLoading(false);
      }
    };
    fetchContent();
  }, []);

  return (
    <div className="bg-white py-12 md:py-20 px-4">
      <div className="container mx-auto max-w-5xl">

        {/* Dynamic Content Section */}
        <section className="mb-16 md:mb-24">
          <h1 className="text-4xl md:text-5xl font-bold text-primary mb-8 text-center">
            About Everything Automotive
          </h1>
          
          {loading ? (
             <div className="flex justify-center py-10">
                <FaSpinner className="animate-spin text-3xl text-primary" />
             </div>
          ) : error ? (
             <div className="text-red-500 bg-red-50 p-4 rounded text-center">
                {error}
             </div>
          ) : (
             <div className="text-lg text-gray-700 max-w-4xl mx-auto">
                {content ? (
                    <ReactMarkdown
                        components={{
                            h1: ({node, ...props}) => <h1 className="text-3xl font-bold text-gray-900 mt-8 mb-4 text-center" {...props}>{props.children}</h1>,
                            h2: ({node, ...props}) => <h2 className="text-2xl font-bold text-gray-800 mt-6 mb-3" {...props}>{props.children}</h2>,
                            h3: ({node, ...props}) => <h3 className="text-xl font-bold text-gray-800 mt-5 mb-2" {...props}>{props.children}</h3>,
                            // Added text-justify here
                            p: ({node, ...props}) => <p className="mb-4 leading-relaxed text-justify" {...props}>{props.children}</p>,
                            ul: ({node, ...props}) => <ul className="list-disc list-inside mb-4 pl-4" {...props}>{props.children}</ul>,
                            li: ({node, ...props}) => <li className="mb-2 text-justify" {...props}>{props.children}</li>,
                            strong: ({node, ...props}) => <strong className="font-bold text-gray-900" {...props}>{props.children}</strong>,
                        }}
                    >
                        {content}
                    </ReactMarkdown>
                ) : (
                    <p className="text-gray-400 italic text-center">
                        (No content available. Please configure "About Us" in the Admin Panel > Content CMS)
                    </p>
                )}
             </div>
          )}
        </section>

        {/* Static Sections Below */}
        <section className="mb-16 md:mb-24">
          <h2 className="text-3xl font-bold text-center text-gray-800 mb-12">Our Comprehensive Services</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-gray-50 p-6 rounded-lg shadow-md text-center hover:shadow-lg transition-shadow duration-300">
              <FaShoppingCart className="text-4xl text-primary mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-800 mb-2">Vast Parts E-commerce</h3>
              <p className="text-gray-600 text-sm text-justify">
                Browse and purchase genuine and high-quality aftermarket parts from trusted stores across Nigeria.
              </p>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg shadow-md text-center hover:shadow-lg transition-shadow duration-300">
              <FaCar className="text-4xl text-primary mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-800 mb-2">Vehicle Marketplace</h3>
              <p className="text-gray-600 text-sm text-justify">
                A secure platform to buy and sell new, Nigerian-used, and foreign-used vehicles with detailed listings.
              </p>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg shadow-md text-center hover:shadow-lg transition-shadow duration-300">
              <FaTools className="text-4xl text-primary mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-800 mb-2">Workshop & Home Service</h3>
              <p className="text-gray-600 text-sm text-justify">
                Book appointments at certified workshops or request convenient vehicle maintenance right at your doorstep.
              </p>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg shadow-md text-center hover:shadow-lg transition-shadow duration-300">
              <FaBrain className="text-4xl text-primary mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-800 mb-2">AI Mechanic Assistant</h3>
              <p className="text-gray-600 text-sm text-justify">
                Get instant diagnostic guidance, part recommendations, and service booking assistance powered by AI.
              </p>
            </div>
          </div>
        </section>

        <section className="bg-primary text-white p-10 md:p-16 rounded-lg shadow-lg mb-16 md:mb-24">
          <h2 className="text-3xl font-bold text-center mb-10">Why Everything Automotive?</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
            <div>
              <FaMapMarkerAlt className="text-4xl mx-auto mb-3 opacity-90" />
              <h4 className="text-xl font-semibold mb-2">Nigeria-Focused</h4>
              <p className="text-sm opacity-90 text-justify">Built specifically for the Nigerian market, understanding local needs and logistics.</p>
            </div>
            <div>
              <FaLightbulb className="text-4xl mx-auto mb-3 opacity-90" />
              <h4 className="text-xl font-semibold mb-2">Innovative Technology</h4>
              <p className="text-sm opacity-90 text-justify">Leveraging AI and a modern platform for a seamless user experience.</p>
            </div>
            <div>
              <FaUsers className="text-4xl mx-auto mb-3 opacity-90" />
              <h4 className="text-xl font-semibold mb-2">Expertise & Trust</h4>
              <p className="text-sm opacity-90 text-justify">Backed by experienced automotive and technology professionals.</p>
            </div>
          </div>
        </section>

        <section className="text-center mb-16 md:mb-24">
           <FaBullseye className="text-5xl text-primary mx-auto mb-5" />
          <h2 className="text-3xl font-bold text-gray-800 mb-4">Our Vision</h2>
          <p className="text-lg text-gray-700 max-w-3xl mx-auto text-justify">
            To be the undisputed leader in Nigeria's online automotive space, providing unparalleled convenience, value, and trust for every vehicle owner and enthusiast.
          </p>
        </section>

        <section className="mb-16 md:mb-24">
            <h2 className="text-3xl font-bold text-center text-gray-800 mb-12">Meet Our Leadership</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-10 max-w-4xl mx-auto">
                <div className="bg-gray-50 p-6 rounded-lg shadow-sm text-center">
                    <div className="w-24 h-24 bg-primary rounded-full mx-auto mb-4 flex items-center justify-center text-white text-4xl">
                        <FaUsers />
                    </div>
                    <h4 className="text-xl font-semibold text-gray-800">Mr. Gabriel Osereime Esekie</h4>
                    <p className="text-primary text-sm mb-2">QSystems Automations Nigeria</p>
                    <p className="text-gray-600 text-sm text-justify">Driving the technological innovation and platform strategy.</p>
                </div>
                 <div className="bg-gray-50 p-6 rounded-lg shadow-sm text-center">
                    <div className="w-24 h-24 bg-primary rounded-full mx-auto mb-4 flex items-center justify-center text-white text-4xl">
                        <FaUsers />
                    </div>
                    <h4 className="text-xl font-semibold text-gray-800">Engr. Thomas Osebha Esekie</h4>
                    <p className="text-primary text-sm mb-2">Global Automotive Concept Nigeria</p>
                    <p className="text-gray-600 text-sm text-justify">Bringing deep automotive expertise and industry knowledge.</p>
                </div>
            </div>
        </section>

        <section className="text-center bg-gray-100 p-10 rounded-lg">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">Ready to Experience the Difference?</h2>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <a href="/parts/categories" className="bg-primary hover:bg-primary-dark text-white font-semibold py-3 px-6 rounded-md transition duration-300">
              Browse Parts
            </a>
            <a href="/services/schedule" className="bg-gray-700 hover:bg-gray-800 text-white font-semibold py-3 px-6 rounded-md transition duration-300">
              Book a Service
            </a>
            <a href="/contact" className="border border-primary text-primary hover:bg-primary hover:text-white font-semibold py-3 px-6 rounded-md transition duration-300">
              Contact Us
            </a>
          </div>
        </section>

      </div>
    </div>
  );
}

export default AboutPage;